import apiService from "@/api-service"
import { BestekFilterParams, initialBestekFilterParams, mapFilterParamsToQueryParams } from "@/components/bestek-filter/bestek-filter-params"
import HeaderedContainer from "@/components/headered-container/headered-container"
import HeaderedList from "@/components/headered-list/headered-list"
import { Deficode } from "@/models/hoofdcode"
import { add, format } from "date-fns"
import { nl } from "date-fns/locale"
import { useEffect, useState } from "react"
import { Col, Container, Form, Row, Spinner } from "react-bootstrap"
import Select from "react-select";
import { ResponsiveContainer, BarChart, YAxis, XAxis, Tooltip, Legend, Bar } from "recharts"
import { DataKey } from "recharts/types/util/types"

interface usage { 
    month: string, 
    aantal: number, 
    aantal_bestekken: number, 
    totale_hoeveelheid: number
}

interface UsageResponse {
    hoofdcode_usage: usage[],
    deficode_usage: {
        [key: string]: {
            deficode: Deficode,
            usage: usage[]
        }
    }
}

interface Props {
    hoofdcodeID: number;
    eenheid: string;
    bestekFilterParams?: BestekFilterParams;
    deficode?: Deficode;
    preloadedData?: UsageResponse;  // NEW: Accept preloaded data
    // Cijfers voor de "Totaal"-box, gescopet op alleen déze jaarversie (zelfde bron als de
    // bestekken-tabel/lijstpagina). De grafiekdata zelf (hoofdcode_usage) telt bewust ook
    // voorgaande jaarversies mee voor een doorlopende tijdlijn — die twee horen dus niet
    // hetzelfde te tonen, vandaar deze losse, correct-gescopete override voor de Totaal-cijfers.
    totaalOverride?: { bestekken: number; vermeldingen: number; hoeveelheid: number };
}

const HoofdcodeUsageChart = (props: Props): JSX.Element => {
    const eenheid = props.deficode ? props.deficode.eenheid : props.eenheid;
    const [ completeUsage, setCompleteUsage ] = useState<UsageResponse>();
    const [ paramHash, setParamHash ] = useState<string>();

    const [ isLoading, setIsLoading ] = useState<boolean>(false);
    
    const fetch = (): void => {
        setIsLoading(true);
        apiService.post<UsageResponse>('stats/hoofdcode-usage', { 
            hoofdcode_id: props.hoofdcodeID, 
            bestek_filter: mapFilterParamsToQueryParams(props.bestekFilterParams ?? initialBestekFilterParams) 
        })
            .then((response) => {
                setCompleteUsage(response.data);
            }, console.error)
            .finally(() => {
                setIsLoading(false);
            })
    }

    useEffect(() => {
        setParamHash(JSON.stringify(props.bestekFilterParams ?? {}));
    })

    // NEW: Use preloaded data on initial load
    useEffect(() => {
        // Check if we should use preloaded data
        const shouldUsePreloadedData = 
            props.preloadedData && 
            !props.bestekFilterParams && 
            !props.deficode &&
            !completeUsage;  // Only on initial load
        
        if (shouldUsePreloadedData) {
            console.log('✅ Using preloaded chart data (no API call needed)');
            setCompleteUsage(props.preloadedData);
            setIsLoading(false);
            return;
        }
        
        // Otherwise fetch normally
        fetch();
    }, [paramHash, props.preloadedData, props.deficode]);

    let usage: usage[] = [];
    let graphData: { label: Date, aantal: number, aantal_bestekken: number, hoeveelheid: number }[] = [];
    if (completeUsage) {
        if (props.deficode) {
            const key = `${props.deficode.positie}:${props.deficode.inhoud_nr}`;
            usage = completeUsage.deficode_usage[key]?.usage ?? [];
        } else {
            usage = completeUsage.hoofdcode_usage;
        }
    }

    if (usage.length > 0) {
        const usageDates: Date[] = usage.map(u => new Date(u.month)).sort((a, b) => a < b ? -1 : 1);
        const firstDate: Date = usageDates.length > 0 ? usageDates[0] : new Date();
        const lastDate: Date = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
        let date = new Date(firstDate);
        while (date <= lastDate) {
            const datapoint = usage.find(p => p.month === format(date, "yyyy-MM-01"));
            if (datapoint) {
                graphData.push({
                    label: new Date(date),
                    aantal: datapoint.aantal,
                    aantal_bestekken: datapoint.aantal_bestekken,
                    hoeveelheid: datapoint.totale_hoeveelheid
    
                });
            } else {
                graphData.push({
                    label: new Date(date),
                    aantal: 0,
                    aantal_bestekken: 0,
                    hoeveelheid: 0
                });
            }
            date = add(date, { months: 1 });
        }
    }

    const labelMap: { [key: string]: string } = {
        aantal: 'Aantal vermeldingen',
        aantal_bestekken: 'Aantal bestekken',
        hoeveelheid: `Hoeveelheid (${eenheid ?? '-'})`
    };

    const defaultVisibilityMap: { [key: string]: boolean } = {
        aantal: true,
        aantal_bestekken: false,
        hoeveelheid: true
    }
    const [ visibilityMap, setVisibilityMap ] = useState<{ [key: string]: boolean }>(defaultVisibilityMap);

    const onLegendItemClick = (data: {dataKey?: DataKey<any> }): void => {
        if (data.dataKey && (data.dataKey as string) in visibilityMap) {
            const vm = { ... visibilityMap };
            vm[data.dataKey as string] = !vm[data.dataKey as string];
            setVisibilityMap(vm);
        }
    }

    const chartComponent = (): JSX.Element => {
        return (
            <ResponsiveContainer height={300} width="100%" minWidth={300}>
                <BarChart data={graphData} margin={{ top: 15 }} barGap={0} barCategoryGap="20%">
                    <YAxis type="number" />
                    <YAxis yAxisId={1} orientation="right" type="number" />
                    <XAxis dataKey={(i) => format(i.label, "MMM yyyy", { locale: nl })} />
                    <Tooltip formatter={(val, label) => [val, labelMap[label]]} />
                    <Legend onClick={onLegendItemClick} formatter={(val) => labelMap[val]} />
                    <Bar dataKey="aantal_bestekken" fill="#8884d8" hide={!visibilityMap['aantal_bestekken']} />
                    <Bar dataKey="aantal" fill="#84d8b2" hide={!visibilityMap['aantal']} />
                    <Bar yAxisId={1} dataKey="hoeveelheid" fill="#d4d884" hide={!visibilityMap['hoeveelheid']} />
                </BarChart>
            </ResponsiveContainer>
        )
    }

    const reduceUsage = (prop: 'aantal_bestekken' | 'aantal' | 'totale_hoeveelheid'): number => {
        const reduced = usage.reduce((total, u) => total += u[prop], 0);

        return Math.round(reduced * 1000) / 1000;
    }
    // Voor de gewone hoofdcode-weergave (niet deficode-specifiek) gebruiken we de meegegeven,
    // op deze jaarversie gescopete cijfers — niet de som van de (bewust bredere) grafiekdata,
    // anders wijkt deze box af van de bestekken-tabel en de lijstpagina eronder/ernaast.
    const totalCountsListItems: { label: string, value: string }[] = props.totaalOverride && !props.deficode
        ? [
            { label: 'Bestekken:', value: props.totaalOverride.bestekken.toLocaleString() },
            { label: 'Vermeldingen:', value: props.totaalOverride.vermeldingen.toLocaleString() },
            { label: 'Hoeveelheid:', value: `${props.totaalOverride.hoeveelheid.toLocaleString()} ${eenheid ? '(' + (eenheid ?? '-') + ')' : ''}` }
        ]
        : [
            { label: 'Bestekken:', value: reduceUsage('aantal_bestekken').toLocaleString() },
            { label: 'Vermeldingen:', value: reduceUsage('aantal').toLocaleString() },
            { label: 'Hoeveelheid:', value: `${reduceUsage('totale_hoeveelheid').toLocaleString()} ${eenheid ? '(' + (eenheid ?? '-') + ')' : ''}` }
        ]

    return (
        <>
        {
            isLoading && (
                <div className="d-flex align-items-center justify-content-center mt-4">
                    <Spinner className="me-4"></Spinner>         
                    <span>Grafiekdata ophalen...</span>
                </div>
            )
        }
        {
            !isLoading && usage && usage.length === 0 && (
                <span>Geen data om weer te geven.</span>
            )
        }
        {
            !isLoading && usage && usage.length > 0 && (
                <Container fluid style={{ width: '100%', padding: 0 }} className="mt-4">
                    <Row>
                        <Col sm={12} md={9}>
                            <HeaderedContainer title={props.deficode ? "Per maand" : "Per maand (incl. voorgaande jaarversies)"}>
                                { chartComponent() }
                            </HeaderedContainer>
                        </Col>

                        <Col sm={12} md={3}>
                            <HeaderedList title="Totaal" items={totalCountsListItems} />
                        </Col>
                    </Row>
                </Container>
            )
        }
        </>
    )
}

export default HoofdcodeUsageChart;